import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import Filter from 'component/filter';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import FlexibleTable from 'component/flexible-table';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch } from 'hooks/redux';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import AddImages from 'component/add-images';
import {
    CardsPersonageData,
    CardsPersonageListRequest,
} from 'types/card/card-personage-list';
import pxToRem from 'assets/theme/function/px-to-rem';
import AddPersonage from 'pages/cards-personage/add-personage';
import EditPersonage from 'pages/cards-personage/edit-personage';
import FilterCardPersonage from 'pages/cards-personage/filter-personage/index';
import AddToPhotosIcon from '@mui/icons-material/FlipCameraIos';
import { useLazyPersonageListQuery } from 'store/slices/cards/personage/personage-list';
import { usePersonageDeleteMutation } from 'store/slices/cards/personage/delete-personage';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { CardData } from 'types/card/card-list';
import { ImageTypeEnum } from 'types/card/card';
import {
    usePersonageBackgroundImageUpdateMutation,
    usePersonageImageUpdateMutation,
} from 'store/slices/cards/personage/add-personage-image';
import { usePersonageActiveMutation } from 'store/slices/cards/personage/active-personage';
import { getStatusColor } from 'component/status-style';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { API_ENDPOINT_URL } from 'constant';
import ViewPhoto from 'component/view-photo';
import { UrlObject } from 'pages/card';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';

export const initialListData: CardsPersonageListRequest = {
    page: 1,
    pageSize: 10,
    sort: 'code',
};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Image',
        isSortable: false,
    },
    {
        key: 'title',
        position: 2,
        title: 'Name',
        isSortable: true,
    },

    {
        key: 'sphereTitle',
        position: 3,
        title: 'Sphere Name',
        isSortable: false,
    },
    {
        key: 'code',
        position: 4,
        title: 'Code Personage',
        isSortable: true,
    },
    {
        key: 'createdAt',
        position: 5,
        title: 'Creation Data',
        isSortable: true,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },
    {
        key: 'status',
        position: 6,
        title: 'Status',
        isSortable: false,
    },

    {
        key: 'action',
        position: 7,
        title: 'Action',
        isSortable: false,
    },
];

const CardsPersonage: React.FC = () => {
    const [openAddCardsPersonageDialog, setOpenAddCardsPersonageDialog] =
        React.useState(false);
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [openEditPersonageDialog, setOpenEditPersonageDialog] =
        React.useState(false);
    const [openImgDialog, setOpenImgDialog] = React.useState(false);
    const [openBackgroundImgDialog, setOpenBackgroundImgDialog] =
        React.useState(false);
    const [personageId, setPersonageId] = React.useState<CardData>();
    const [personageStatus, setPersonageStatus] = React.useState<string>('');
    const [dataPersonageRequestList, setDataPersonageRequestList] =
        React.useState<CardsPersonageListRequest>(initialListData);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(initialListData?.page);
    const [pageSize, setPageSize] = useState<number>(initialListData?.pageSize);
    const [sort, setSort] = useState<string>(initialListData.sort);
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [viewPhotoUrl, setViewPhotoUrl] = useState<UrlObject | null>(null);

    const [
        personageImagesUpdateApi,
        {
            error: imagesError,
            isError: imagesIsError,
            isSuccess: imagesIsSuccess,
        },
    ] = usePersonageImageUpdateMutation();

    const [
        personageBackgroundImagesUpdateApi,
        {
            error: imagesBgError,
            isError: imagesBgIsError,
            isSuccess: imagesBgIsSuccess,
        },
    ] = usePersonageBackgroundImageUpdateMutation();

    const [getPersonageList, { data, isFetching }] =
        useLazyPersonageListQuery();

    const [
        personageDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = usePersonageDeleteMutation();

    const [
        statusActiveApi,
        { error: activeError, isError: activeIsError, isSuccess },
    ] = usePersonageActiveMutation();

    const dispatch = useAppDispatch();

    const createNewSphere = () => {
        setOpenAddCardsPersonageDialog(true);
    };

    const updateImages = (images: FormData) => {
        personageImagesUpdateApi({
            cardPersonageId: personageId?.id,
            image: images,
        });
    };

    const updateBackgroundImages = (images: FormData) => {
        personageBackgroundImagesUpdateApi({
            cardPersonageId: personageId?.id,
            image: images,
        });
    };

    const deletePersonage = ({ id }: CardsPersonageData) => {
        if (id) {
            personageDeleteApi(id);
        }
    };

    const getPersonageListFunction = () => {
        getPersonageList({
            ...dataPersonageRequestList,
            sort: sort === '' ? initialListData.sort : sort,
            page: page,
            pageSize: pageSize,
        });
    };

    const handelClickEditCardPersonage = (personageId: CardsPersonageData) => {
        setOpenEditPersonageDialog(true);
        setPersonageId(personageId);
        setPersonageStatus(personageId.status ?? '');
    };

    const handelClickStatus = (personageId: string) => {
        statusActiveApi(personageId);
    };

    const openPictureDialogFun = (personageId: CardsPersonageData) => {
        setPersonageId(personageId);
        setOpenImgDialog(true);
    };

    const openBackgroundPictureDialogFun = (
        personageId: CardsPersonageData,
    ) => {
        setPersonageId(personageId);
        setOpenBackgroundImgDialog(true);
    };

    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            data && data?.data.length > 0 ? Number(data?.rowsCount) : 0,
        );
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      status: (
                          <Box>
                              <Button
                                  variant={'contained'}
                                  size={'small'}
                                  sx={{
                                      bgcolor: getStatusColor(fullInfo.status),
                                      pointerEvents:
                                          fullInfo.status !== 'draft'
                                              ? 'none'
                                              : 'auto',
                                  }}
                                  onClick={() => {
                                      dispatch(
                                          openConfirmationDialogs({
                                              textInfo:
                                                  'Are You Sure Want To Active Personage',
                                              handleConfirm: () =>
                                                  handelClickStatus(
                                                      fullInfo.id,
                                                  ),
                                          }),
                                      );
                                  }}
                              >
                                  {fullInfo.status}
                              </Button>
                          </Box>
                      ),

                      img: (
                          <>
                              {fullInfo?.fileId ? (
                                  <Box
                                      component="img"
                                      src={`${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.PERSONAGE}`}
                                      alt=""
                                      onClick={() => {
                                          setViewPhotoUrl({
                                              url: `${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.PERSONAGE}`,
                                              title: fullInfo.title,
                                          });
                                      }}
                                      sx={{
                                          cursor: 'pointer',
                                          width: 100,
                                          height: 80,
                                          objectFit: 'contain',
                                          objectPosition: 'top',
                                          borderRadius: borders.borderRadius.md,
                                      }}
                                  />
                              ) : (
                                  <Typography variant={'h5'}>-</Typography>
                              )}
                          </>
                      ),
                      action: (
                          <>
                              <Box
                                  display={'flex'}
                                  justifyContent={'center'}
                                  gap={2}
                              >
                                  {fullInfo.status === 'draft' && (
                                      <Tooltip title="Delete">
                                          <Delete
                                              cursor="pointer"
                                              onClick={() => {
                                                  dispatch(
                                                      openConfirmationDialogs({
                                                          textInfo:
                                                              'Are You Sure Want To Delete Personage',
                                                          handleConfirm:
                                                              deletePersonage,
                                                          parameters: {
                                                              id: fullInfo.id,
                                                          },
                                                      }),
                                                  );
                                              }}
                                          />
                                      </Tooltip>
                                  )}
                                  <Tooltip title="Edit">
                                      <Edit
                                          cursor="pointer"
                                          onClick={() =>
                                              handelClickEditCardPersonage({
                                                  fileId: fullInfo.id,
                                                  id: fullInfo.id,
                                                  status: fullInfo.status,
                                              })
                                          }
                                      />
                                  </Tooltip>
                                  <Tooltip title="Add Image">
                                      <AddPhotoAlternateIcon
                                          cursor="pointer"
                                          onClick={() =>
                                              openPictureDialogFun({
                                                  fileId: fullInfo.fileId,
                                                  id: fullInfo.id,
                                                  imageType:
                                                      ImageTypeEnum.PERSONAGE,
                                              })
                                          }
                                      />
                                  </Tooltip>

                                  <Tooltip title="Add Image reverse">
                                      <AddToPhotosIcon
                                          cursor="pointer"
                                          onClick={() =>
                                              openBackgroundPictureDialogFun({
                                                  fileId: fullInfo.backgroundFileId,
                                                  id: fullInfo.id,
                                                  imageType:
                                                      ImageTypeEnum.PERSONAGE,
                                              })
                                          }
                                      />
                                  </Tooltip>
                              </Box>
                          </>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (dataPersonageRequestList) {
            getPersonageListFunction();
        }
    }, [dataPersonageRequestList, page, pageSize, sort]);

    useEffect(() => {
        if (
            deleteIsSuccess ||
            imagesIsSuccess ||
            imagesBgIsSuccess ||
            isSuccess
        ) {
            setOpenImgDialog(false);
            setOpenBackgroundImgDialog(false);
            getPersonageList(dataPersonageRequestList);
        }
    }, [deleteIsSuccess, imagesIsSuccess, imagesBgIsSuccess, isSuccess]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching]);

    useEffect(() => {
        const error =
            deleteError || imagesError || activeError || imagesBgError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [deleteIsError, imagesIsError, activeIsError, imagesBgIsError]);

    return (
        <Box>
            <Filter>
                <FilterCardPersonage
                    setDataPersonageRequestList={setDataPersonageRequestList}
                />
            </Filter>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={createNewSphere}
                    buttonName={'Creat New Personage'}
                />

                {pagesCount > 0 && (
                    <Stack spacing={2} alignItems={'end'}>
                        <TablePagination
                            rowsPerPageOptions={[10, 30, 50]}
                            component="div"
                            count={pagesCount}
                            rowsPerPage={pageSize}
                            page={page - 1}
                            labelRowsPerPage={'Number of rows'}
                            slotProps={{
                                select: {
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    MenuProps: {
                                        disablePortal: true,
                                        slotProps: {
                                            paper: {
                                                sx: {
                                                    backgroundColor:
                                                        colors.secondary.main,
                                                    padding: 0,
                                                },
                                            },
                                        },
                                    },
                                    native: false,
                                },
                            }}
                            onPageChange={(_, page) => handleFilterData(page)}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                )}
            </Box>
            <AddPersonage
                refetch={getPersonageListFunction}
                openDialog={openAddCardsPersonageDialog}
                setOpenDialog={setOpenAddCardsPersonageDialog}
            />

            {personageId?.id && (
                <>
                    <Dialog
                        open={openImgDialog}
                        onClose={() => {
                            setOpenImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={[
                                personageId.fileId
                                    ? `${API_ENDPOINT_URL}/file/file/${personageId.fileId}?source=${personageId.imageType}`
                                    : undefined,
                            ]}
                            setOpenImgDialog={setOpenImgDialog}
                            onSubmitFun={updateImages}
                            dialogTitle={'NEW PERSONAGE IMAGE'}
                        />
                    </Dialog>

                    <Dialog
                        open={openBackgroundImgDialog}
                        onClose={() => {
                            setOpenBackgroundImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={[
                                personageId.fileId
                                    ? `${API_ENDPOINT_URL}/file/file/${personageId.fileId}?source=${personageId.imageType}`
                                    : undefined,
                            ]}
                            setOpenImgDialog={setOpenBackgroundImgDialog}
                            onSubmitFun={updateBackgroundImages}
                            dialogTitle={'UPLOAD IMAGE'}
                        />
                    </Dialog>

                    <Dialog
                        open={openEditPersonageDialog}
                        onClose={() => {
                            setOpenEditPersonageDialog(false);
                        }}
                    >
                        <EditPersonage
                            refetch={getPersonageListFunction}
                            personageId={personageId.fileId}
                            setOpenEditPersonageDialog={
                                setOpenEditPersonageDialog
                            }
                            personageStatus={personageStatus}
                        />
                    </Dialog>
                </>
            )}

            {viewPhotoUrl && (
                <ViewPhoto
                    open={Boolean(viewPhotoUrl)}
                    setOpen={() => setViewPhotoUrl(null)}
                    imageUrl={viewPhotoUrl}
                />
            )}
        </Box>
    );
};

export default CardsPersonage;
